import React, { useState } from 'react';
import { Shield, FileText, AlertTriangle, CheckCircle, TrendingUp, BarChart3, Search, Download } from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';

const ComplianceReports = () => {
  const [activeTab, setActiveTab] = useState('compliance-monitoring');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Mock data for compliance monitoring
  const complianceMetrics = [
    {
      id: 1,
      framework: 'SOX',
      status: 'Compliant',
      score: 95,
      lastAudit: '2024-01-15',
      nextAudit: '2024-04-15',
      issues: 2,
      criticalIssues: 0,
      department: 'Finance'
    },
    {
      id: 2,
      framework: 'GDPR',
      status: 'Compliant',
      score: 88,
      lastAudit: '2024-01-10',
      nextAudit: '2024-07-10',
      issues: 5,
      criticalIssues: 1,
      department: 'Legal'
    },
    {
      id: 3,
      framework: 'ISO 27001',
      status: 'Under Review',
      score: 76,
      lastAudit: '2024-01-05',
      nextAudit: '2024-03-05',
      issues: 8,
      criticalIssues: 2,
      department: 'IT'
    }
  ];

  // Mock data for KPI tracking
  const kpiMetrics = [
    {
      id: 1,
      kpi: 'Data Breach Incidents',
      current: 0,
      target: 0,
      trend: 'Stable',
      period: 'Q1 2024',
      status: 'On Track',
      category: 'Security'
    },
    {
      id: 2,
      kpi: 'Audit Completion Rate',
      current: 95,
      target: 100,
      trend: 'Improving',
      period: 'Q1 2024',
      status: 'Near Target',
      category: 'Audit'
    },
    {
      id: 3,
      kpi: 'Policy Compliance Rate',
      current: 87,
      target: 95,
      trend: 'Declining',
      period: 'Q1 2024',
      status: 'Needs Attention',
      category: 'Policy'
    },
    {
      id: 4,
      kpi: 'Training Completion Rate',
      current: 92,
      target: 90,
      trend: 'Improving',
      period: 'Q1 2024',
      status: 'Exceeding',
      category: 'Training'
    }
  ];

  // Mock data for regulatory reports
  const regulatoryReports = [
    {
      id: 1,
      reportName: 'SOX Compliance Report',
      framework: 'SOX',
      period: 'Q4 2023',
      status: 'Submitted',
      dueDate: '2024-01-31',
      submittedDate: '2024-01-25',
      approver: 'CFO',
      findings: 3,
      recommendations: 5
    },
    {
      id: 2,
      reportName: 'GDPR Data Protection Report',
      framework: 'GDPR',
      period: 'Annual 2023',
      status: 'Draft',
      dueDate: '2024-02-28',
      submittedDate: null,
      approver: 'DPO',
      findings: 7,
      recommendations: 12
    },
    {
      id: 3,
      reportName: 'ISO 27001 Security Assessment',
      framework: 'ISO 27001',
      period: 'Q1 2024',
      status: 'In Progress',
      dueDate: '2024-03-15',
      submittedDate: null,
      approver: 'CISO',
      findings: 4,
      recommendations: 8
    }
  ];

  // Mock data for audit findings
  const auditFindings = [
    {
      id: 1,
      finding: 'Insufficient access controls on financial systems',
      severity: 'High',
      category: 'Access Control',
      framework: 'SOX',
      status: 'Open',
      assignedTo: 'IT Security Team',
      dueDate: '2024-02-15',
      description: 'Financial systems lack proper role-based access controls'
    },
    {
      id: 2,
      finding: 'Data retention policy not consistently applied',
      severity: 'Medium',
      category: 'Data Management',
      framework: 'GDPR',
      status: 'In Progress',
      assignedTo: 'Data Governance Team',
      dueDate: '2024-02-28',
      description: 'Personal data is being retained longer than policy allows'
    },
    {
      id: 3,
      finding: 'Security awareness training overdue',
      severity: 'Low',
      category: 'Training',
      framework: 'ISO 27001',
      status: 'Closed',
      assignedTo: 'HR Department',
      dueDate: '2024-01-31',
      description: 'Annual security training was completed on time'
    }
  ];

  const tabs = [
    { id: 'compliance-monitoring', name: 'Compliance Monitoring', icon: <Shield size={18} /> },
    { id: 'kpi-tracking', name: 'KPI Tracking', icon: <TrendingUp size={18} /> },
    { id: 'regulatory-reports', name: 'Regulatory Reports', icon: <FileText size={18} /> },
    { id: 'audit-findings', name: 'Audit Findings', icon: <AlertTriangle size={18} /> }
  ];

  const handleAction = (action, data) => {
    setModalData({ action, data });
    setShowModal(true);
  };

  const handleConfirm = () => {
    setShowModal(false);
    setModalData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Compliant': return { color: '#059669', backgroundColor: '#f0fdf4' };
      case 'Under Review': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Non-Compliant': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'On Track': return { color: '#059669', backgroundColor: '#f0fdf4' };
      case 'Near Target': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Needs Attention': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'Exceeding': return { color: '#059669', backgroundColor: '#f0fdf4' };
      case 'Submitted': return { color: '#059669', backgroundColor: '#f0fdf4' };
      case 'Draft': return { color: '#6b7280', backgroundColor: '#f9fafb' };
      case 'In Progress': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Open': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'In Progress': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Closed': return { color: '#059669', backgroundColor: '#f0fdf4' };
      default: return { color: '#6b7280', backgroundColor: '#f9fafb' };
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return { color: '#dc2626', backgroundColor: '#fef2f2' };
      case 'Medium': return { color: '#d97706', backgroundColor: '#fffbeb' };
      case 'Low': return { color: '#059669', backgroundColor: '#f0fdf4' };
      default: return { color: '#6b7280', backgroundColor: '#f9fafb' };
    }
  };

  const renderComplianceMonitoring = () => (
    <div className="page-tab-content">
      <div className="page-table-container">
        <DataTable
          data={complianceMetrics}
          title="Compliance Monitoring"
          showCheckboxes={false}
          columns={[
            {
              key: 'framework',
              label: 'Framework'
            },
            {
              key: 'status',
              label: 'Status',
              render: (item) => {
                const statusClass = item.status === 'Compliant' ? 'badge-success' : 
                                  item.status === 'Under Review' ? 'badge-warning' : 
                                  item.status === 'Non-Compliant' ? 'badge-danger' : 
                                  item.status === 'On Track' ? 'badge-success' :
                                  item.status === 'Near Target' ? 'badge-warning' :
                                  item.status === 'Needs Attention' ? 'badge-danger' :
                                  item.status === 'Exceeding' ? 'badge-success' :
                                  item.status === 'Submitted' ? 'badge-success' :
                                  item.status === 'Draft' ? 'badge-secondary' :
                                  item.status === 'In Progress' ? 'badge-warning' :
                                  item.status === 'Open' ? 'badge-danger' :
                                  item.status === 'Closed' ? 'badge-success' : 'badge-secondary';
                return (
                  <span className={`badge ${statusClass}`}>
                    {item.status}
                  </span>
                );
              }
            },
            {
              key: 'score',
              label: 'Score',
              render: (item) => (
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium">{item.score}%</span>
                  <div className="progress" style={{ width: '60px', height: '6px' }}>
                    <div 
                      className={`progress-bar ${item.score >= 90 ? 'bg-success' : item.score >= 70 ? 'bg-warning' : 'bg-danger'}`}
                      style={{ width: `${item.score}%` }}
                    ></div>
                  </div>
                </div>
              )
            },
            {
              key: 'issues',
              label: 'Issues'
            },
            {
              key: 'criticalIssues',
              label: 'Critical Issues'
            },
            {
              key: 'lastAudit',
              label: 'Last Audit'
            },
            {
              key: 'nextAudit',
              label: 'Next Audit'
            }
          ]}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('generate-report', item)}
        />
      </div>
    </div>
  );

  const renderKPITracking = () => (
    <div className="page-tab-content">
      <div className="page-table-container">
        <DataTable
          data={kpiMetrics}
          title="KPI Tracking"
          showCheckboxes={false}
          columns={[
            {
              key: 'kpi',
              label: 'KPI'
            },
            {
              key: 'category',
              label: 'Category'
            },
            {
              key: 'current',
              label: 'Current Value'
            },
            {
              key: 'target',
              label: 'Target'
            },
            {
              key: 'trend',
              label: 'Trend',
              render: (item) => (
                <div className="d-flex align-items-center gap-1">
                  <TrendingUp size={14} color={item.trend === 'Improving' ? '#059669' : item.trend === 'Declining' ? '#dc2626' : '#6b7280'} />
                  <span className="fw-medium">{item.trend}</span>
                </div>
              )
            },
            {
              key: 'status',
              label: 'Status',
              render: (item) => {
                const statusClass = item.status === 'Compliant' ? 'badge-success' : 
                                  item.status === 'Under Review' ? 'badge-warning' : 
                                  item.status === 'Non-Compliant' ? 'badge-danger' : 
                                  item.status === 'On Track' ? 'badge-success' :
                                  item.status === 'Near Target' ? 'badge-warning' :
                                  item.status === 'Needs Attention' ? 'badge-danger' :
                                  item.status === 'Exceeding' ? 'badge-success' :
                                  item.status === 'Submitted' ? 'badge-success' :
                                  item.status === 'Draft' ? 'badge-secondary' :
                                  item.status === 'In Progress' ? 'badge-warning' :
                                  item.status === 'Open' ? 'badge-danger' :
                                  item.status === 'Closed' ? 'badge-success' : 'badge-secondary';
                return (
                  <span className={`badge ${statusClass}`}>
                    {item.status}
                  </span>
                );
              }
            },
            {
              key: 'period',
              label: 'Period'
            }
          ]}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('update-kpi', item)}
        />
      </div>
    </div>
  );

  const renderRegulatoryReports = () => (
    <div className="page-tab-content">
      <div className="page-table-container">
        <DataTable
          data={regulatoryReports}
          title="Regulatory Reports"
          showCheckboxes={false}
          columns={[
            {
              key: 'reportName',
              label: 'Report Name'
            },
            {
              key: 'framework',
              label: 'Framework'
            },
            {
              key: 'period',
              label: 'Period'
            },
            {
              key: 'status',
              label: 'Status',
              render: (item) => {
                if (!item) return <span style={{ fontSize: '12px', color: '#6b7280' }}>N/A</span>;
                const statusClass = item.status === 'Compliant' ? 'badge-success' : 
                                  item.status === 'Under Review' ? 'badge-warning' : 
                                  item.status === 'Non-Compliant' ? 'badge-danger' : 
                                  item.status === 'On Track' ? 'badge-success' :
                                  item.status === 'Near Target' ? 'badge-warning' :
                                  item.status === 'Needs Attention' ? 'badge-danger' :
                                  item.status === 'Exceeding' ? 'badge-success' :
                                  item.status === 'Submitted' ? 'badge-success' :
                                  item.status === 'Draft' ? 'badge-secondary' :
                                  item.status === 'In Progress' ? 'badge-warning' :
                                  item.status === 'Open' ? 'badge-danger' :
                                  item.status === 'Closed' ? 'badge-success' : 'badge-secondary';
                return (
                  <span className={`badge ${statusClass}`}>
                    {item.status}
                  </span>
                );
              }
            },
            {
              key: 'dueDate',
              label: 'Due Date'
            },
            {
              key: 'submittedDate',
              label: 'Submitted Date',
              render: (item) => item && item.submittedDate ? item.submittedDate : 'Not submitted'
            },
            {
              key: 'findings',
              label: 'Findings'
            }
          ]}
          onView={(item) => handleAction('view-report', item)}
          onEdit={(item) => handleAction('download-report', item)}
        />
      </div>
    </div>
  );

  const renderAuditFindings = () => (
    <div className="page-tab-content">
      <div className="page-table-container">
        <DataTable
          data={auditFindings}
          title="Audit Findings"
          showCheckboxes={false}
          columns={[
            {
              key: 'finding',
              label: 'Finding'
            },
            {
              key: 'severity',
              label: 'Severity',
              render: (item) => {
                const severityClass = item.severity === 'High' ? 'badge-danger' : 
                                    item.severity === 'Medium' ? 'badge-warning' : 
                                    item.severity === 'Low' ? 'badge-success' : 'badge-secondary';
                return (
                  <span className={`badge ${severityClass}`}>
                    {item.severity}
                  </span>
                );
              }
            },
            {
              key: 'category',
              label: 'Category'
            },
            {
              key: 'framework',
              label: 'Framework'
            },
            {
              key: 'status',
              label: 'Status',
              render: (item) => {
                const statusClass = item.status === 'Compliant' ? 'badge-success' : 
                                  item.status === 'Under Review' ? 'badge-warning' : 
                                  item.status === 'Non-Compliant' ? 'badge-danger' : 
                                  item.status === 'On Track' ? 'badge-success' :
                                  item.status === 'Near Target' ? 'badge-warning' :
                                  item.status === 'Needs Attention' ? 'badge-danger' :
                                  item.status === 'Exceeding' ? 'badge-success' :
                                  item.status === 'Submitted' ? 'badge-success' :
                                  item.status === 'Draft' ? 'badge-secondary' :
                                  item.status === 'In Progress' ? 'badge-warning' :
                                  item.status === 'Open' ? 'badge-danger' :
                                  item.status === 'Closed' ? 'badge-success' : 'badge-secondary';
                return (
                  <span className={`badge ${statusClass}`}>
                    {item.status}
                  </span>
                );
              }
            },
            {
              key: 'assignedTo',
              label: 'Assigned To'
            },
            {
              key: 'dueDate',
              label: 'Due Date'
            }
          ]}
          onView={(item) => handleAction('view-finding', item)}
          onEdit={(item) => handleAction('update-status', item)}
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'compliance-monitoring':
        return renderComplianceMonitoring();
      case 'kpi-tracking':
        return renderKPITracking();
      case 'regulatory-reports':
        return renderRegulatoryReports();
      case 'audit-findings':
        return renderAuditFindings();
      default:
        return renderComplianceMonitoring();
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Compliance Reports
        </h1>
        <p className="page-subtitle">
          Compliance monitoring, KPI tracking, and regulatory reports
        </p>
      </div>

      {/* KPI Cards */}
      <div className="page-stats-grid">
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-1">{complianceMetrics.length}</h3>
                  <p className="page-kpi-label">Total Frameworks</p>
                </div>
                <div className="page-kpi-icon-container color-1">
                  <Shield className="page-kpi-icon color-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-2">{complianceMetrics.filter(c => c.status === 'Compliant').length}</h3>
                  <p className="page-kpi-label">Compliant Frameworks</p>
                </div>
                <div className="page-kpi-icon-container color-2">
                  <CheckCircle className="page-kpi-icon color-2" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-4">{complianceMetrics.reduce((sum, c) => sum + c.criticalIssues, 0)}</h3>
                  <p className="page-kpi-label">Critical Issues</p>
                </div>
                <div className="page-kpi-icon-container color-4">
                  <AlertTriangle className="page-kpi-icon color-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-5">{Math.round(complianceMetrics.reduce((sum, c) => sum + c.score, 0) / complianceMetrics.length)}%</h3>
                  <p className="page-kpi-label">Average Score</p>
                </div>
                <div className="page-kpi-icon-container color-5">
                  <BarChart3 className="page-kpi-icon color-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map((tab) => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="page-tab-icon">
                  {tab.icon}
                </div>
                {tab.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        title={modalData?.action ? `Confirm ${modalData.action}` : 'Confirm Action'}
        message={modalData?.data ? `Are you sure you want to ${modalData.action} ${modalData.data.framework || modalData.data.kpi || modalData.data.reportName || modalData.data.finding || 'this item'}?` : 'Are you sure you want to perform this action?'}
      />
    </div>
  );
};

export default ComplianceReports;
